import * as React from "react"
import { Tab, TabList, TabPanel, Tabs } from "react-tabs"
import "react-tabs/style/react-tabs.css"
import Cookies from "universal-cookie"
import ManageJobList from "./ManageJobList"
import type { Job, JobType, PositionType, Practice } from "./ManageJobList"

const cookies = new Cookies()

interface PaginationMeta {
  current_page: number;
  total_pages: number;
  total_count: number;
  per_page: number;
}

interface ManageProps {
  jobs: Job[];
  temp_jobs: Job[];
  position_types: PositionType[];
  job_types: JobType[];
  practices: Practice[];
  perm_pagination: PaginationMeta;
  temp_pagination: PaginationMeta;
  jobs_remaining?: {
    remaining: number;
    total: number;
  } | null;
}

interface ManageState {
  jobs: Job[];
  temp_jobs: Job[];
  permPagination: PaginationMeta;
  tempPagination: PaginationMeta;
}

interface FilterState {
  jobTitleFilter: string;
  practiceFilter: string;
  statusFilter: string;
}

interface SortState {
  column: string;
  direction: 'asc' | 'desc';
}

export default function Manage({
  jobs,
  temp_jobs,
  position_types,
  job_types,
  practices,
  perm_pagination,
  temp_pagination,
  jobs_remaining,
}: ManageProps) {
  const [permPage, setPermPage] = React.useState(perm_pagination.current_page)
  const [tempPage, setTempPage] = React.useState(temp_pagination.current_page)
  const [perPage, setPerPage] = React.useState(perm_pagination.per_page)

  // Filter state for perm and temp tabs
  const [permFilters, setPermFilters] = React.useState<FilterState>({
    jobTitleFilter: '',
    practiceFilter: '',
    statusFilter: ''
  })
  const [tempFilters, setTempFilters] = React.useState<FilterState>({
    jobTitleFilter: '',
    practiceFilter: '',
    statusFilter: ''
  })

  // Loading states for filter operations
  const [permLoading, setPermLoading] = React.useState(false)
  // Set whenever a manage.json fetch fails; rendered as a visible alert.
  // Before this, every failure path ended in a silent console.error (or an
  // ErrorBoundary rendering nothing when undefined.map threw), so the page
  // showed an empty jobs list with no explanation — Sentry DIRECTDENTAL-293.
  const [fetchError, setFetchError] = React.useState<string | null>(null)

  // All manage.json calls go through here. Guarantees:
  //  - a 401 (expired session) redirects to sign-in instead of feeding the
  //    devise error JSON ({error: ...} with no job arrays) into .map
  //  - any other non-OK response throws with its status
  //  - perm_jobs / temp_jobs are always arrays on the returned payload
  const fetchManageJson = React.useCallback(async (url: string) => {
    const res = await fetch(url)
    if (res.status === 401) {
      const body = await res.json().catch(() => null)
      window.location.assign((body && body.redirect_to) || '/users/sign_in')
      // Halt the caller; navigation is underway.
      throw new Error('Session expired, redirecting to sign in')
    }
    if (!res.ok) throw new Error(`Request failed (${res.status})`)
    const data = await res.json()
    return {
      ...data,
      perm_jobs: Array.isArray(data.perm_jobs) ? data.perm_jobs : [],
      temp_jobs: Array.isArray(data.temp_jobs) ? data.temp_jobs : []
    }
  }, [])
  const [tempLoading, setTempLoading] = React.useState(false)

  // Sort state for perm and temp tabs
  const [permSort, setPermSort] = React.useState<SortState>({ column: 'createdAt', direction: 'desc' })
  const [tempSort, setTempSort] = React.useState<SortState>({ column: 'createdAt', direction: 'desc' })

  const [state, setState] = React.useState<ManageState>({
    jobs,
    temp_jobs,
    permPagination: perm_pagination,
    tempPagination: temp_pagination
  })

  // Helper to build filter query params
  const buildFilterParams = (filters: FilterState, sort?: SortState): string => {
    const params = new URLSearchParams()
    if (filters.jobTitleFilter) params.append('job_title_filter', filters.jobTitleFilter)
    if (filters.practiceFilter) params.append('practice_filter', filters.practiceFilter)
    if (filters.statusFilter) params.append('status_filter', filters.statusFilter)
    if (sort) {
      params.append('sort_column', sort.column)
      params.append('sort_direction', sort.direction)
    }
    return params.toString()
  }

  const fetchJobs = React.useCallback(async (page?: number, perPageOverride?: number) => {
    try {
      const currentPermPage = page !== undefined ? page : permPage
      const currentTempPage = page !== undefined ? page : tempPage
      const currentPerPage = perPageOverride !== undefined ? perPageOverride : perPage

      // Build URL with filter and sort params
      const permFilterParams = buildFilterParams(permFilters, permSort)

      // For the periodic refresh, use current filter and sort states
      const baseParams = `page=${currentPermPage}&per_page=${currentPerPage}`
      const permParams = permFilterParams ? `${baseParams}&${permFilterParams}` : baseParams

      const data = await fetchManageJson(`/jobs/manage.json?${permParams}`)

      const temp_jobs = data.temp_jobs.map(job => ({
        ...job,
        temp_date: job.temp_date === null ? job.published_at : job.temp_date,
        practice: job.practice || { id: job.practice_id },
        job_type: job.job_type
      }))

      const perm_jobs = data.perm_jobs.map(job => ({
        ...job,
        practice: job.practice || { id: job.practice_id },
        job_type: job.job_type
      }))

      setFetchError(null)

      setState(prevState => ({
        ...prevState,
        jobs: perm_jobs,
        temp_jobs,
        permPagination: data.perm_pagination || prevState.permPagination,
        tempPagination: data.temp_pagination || prevState.tempPagination
      }))
    } catch (error) {
      console.error('Error fetching jobs:', error)
      setFetchError(error instanceof Error ? error.message : 'Could not load your jobs')
    }
  }, [permPage, tempPage, perPage, permFilters, tempFilters, permSort, tempSort])

  const index = window.location.hash.substr(1) === "temp" ? 1 : 0

  const onSelect = (i: number) => {
    history.replaceState(undefined, undefined, i === 1 ? "#temp" : "#perm")
  }

  const handlePermPageChange = React.useCallback(async (page: number) => {
    setPermPage(page)
    try {
      const filterParams = buildFilterParams(permFilters, permSort)
      const baseParams = `page=${page}&per_page=${perPage}`
      const url = filterParams ? `/jobs/manage.json?${baseParams}&${filterParams}` : `/jobs/manage.json?${baseParams}`
      const data = await fetchManageJson(url)
      setFetchError(null)
      setState(prev => ({
        ...prev,
        jobs: data.perm_jobs.map(job => ({
          ...job,
          practice: job.practice || { id: job.practice_id },
          job_type: job.job_type
        })),
        permPagination: data.perm_pagination || prev.permPagination
      }))
    } catch (error) {
      console.error('Error fetching perm jobs:', error)
      setFetchError(error instanceof Error ? error.message : 'Could not load your jobs')
    }
  }, [perPage, permFilters, permSort])

  const handleTempPageChange = React.useCallback(async (page: number) => {
    setTempPage(page)
    try {
      const filterParams = buildFilterParams(tempFilters, tempSort)
      const baseParams = `page=${page}&per_page=${perPage}`
      const url = filterParams ? `/jobs/manage.json?${baseParams}&${filterParams}` : `/jobs/manage.json?${baseParams}`
      const data = await fetchManageJson(url)
      setFetchError(null)
      setState(prev => ({
        ...prev,
        temp_jobs: data.temp_jobs.map(job => ({
          ...job,
          temp_date: job.temp_date === null ? job.published_at : job.temp_date,
          practice: job.practice || { id: job.practice_id },
          job_type: job.job_type
        })),
        tempPagination: data.temp_pagination || prev.tempPagination
      }))
    } catch (error) {
      console.error('Error fetching temp jobs:', error)
      setFetchError(error instanceof Error ? error.message : 'Could not load your jobs')
    }
  }, [perPage, tempFilters, tempSort])

  const handlePerPageChange = React.useCallback(async (newPerPage: number, page: number) => {
    setPerPage(newPerPage)
    setPermPage(page)
    setTempPage(page)
    await fetchJobs(page, newPerPage)
  }, [fetchJobs])

  // Filter change handlers - reset to page 1 and refetch with new filters
  const handlePermFiltersChange = React.useCallback(async (newFilters: FilterState) => {
    setPermFilters(newFilters)
    setPermPage(1)
    setPermLoading(true)
    try {
      const filterParams = buildFilterParams(newFilters, permSort)
      const baseParams = `page=1&per_page=${perPage}`
      const url = filterParams ? `/jobs/manage.json?${baseParams}&${filterParams}` : `/jobs/manage.json?${baseParams}`
      const data = await fetchManageJson(url)
      setFetchError(null)
      setState(prev => ({
        ...prev,
        jobs: data.perm_jobs.map(job => ({
          ...job,
          practice: job.practice || { id: job.practice_id },
          job_type: job.job_type
        })),
        permPagination: data.perm_pagination || prev.permPagination
      }))
    } catch (error) {
      console.error('Error fetching filtered perm jobs:', error)
      setFetchError(error instanceof Error ? error.message : 'Could not load your jobs')
    } finally {
      setPermLoading(false)
    }
  }, [perPage, permSort])

  const handleTempFiltersChange = React.useCallback(async (newFilters: FilterState) => {
    setTempFilters(newFilters)
    setTempPage(1)
    setTempLoading(true)
    try {
      const filterParams = buildFilterParams(newFilters, tempSort)
      const baseParams = `page=1&per_page=${perPage}`
      const url = filterParams ? `/jobs/manage.json?${baseParams}&${filterParams}` : `/jobs/manage.json?${baseParams}`
      const data = await fetchManageJson(url)
      setFetchError(null)
      setState(prev => ({
        ...prev,
        temp_jobs: data.temp_jobs.map(job => ({
          ...job,
          temp_date: job.temp_date === null ? job.published_at : job.temp_date,
          practice: job.practice || { id: job.practice_id },
          job_type: job.job_type
        })),
        tempPagination: data.temp_pagination || prev.tempPagination
      }))
    } catch (error) {
      console.error('Error fetching filtered temp jobs:', error)
      setFetchError(error instanceof Error ? error.message : 'Could not load your jobs')
    } finally {
      setTempLoading(false)
    }
  }, [perPage, tempSort])

  // Sort change handlers - refetch with new sort
  const handlePermSortChange = React.useCallback(async (column: string, direction: 'asc' | 'desc') => {
    const newSort = { column, direction }
    setPermSort(newSort)
    setPermLoading(true)
    try {
      const filterParams = buildFilterParams(permFilters, newSort)
      const baseParams = `page=${permPage}&per_page=${perPage}`
      const url = filterParams ? `/jobs/manage.json?${baseParams}&${filterParams}` : `/jobs/manage.json?${baseParams}`
      const data = await fetchManageJson(url)
      setFetchError(null)
      setState(prev => ({
        ...prev,
        jobs: data.perm_jobs.map(job => ({
          ...job,
          practice: job.practice || { id: job.practice_id },
          job_type: job.job_type
        })),
        permPagination: data.perm_pagination || prev.permPagination
      }))
    } catch (error) {
      console.error('Error fetching sorted perm jobs:', error)
      setFetchError(error instanceof Error ? error.message : 'Could not load your jobs')
    } finally {
      setPermLoading(false)
    }
  }, [perPage, permPage, permFilters])

  const handleTempSortChange = React.useCallback(async (column: string, direction: 'asc' | 'desc') => {
    const newSort = { column, direction }
    setTempSort(newSort)
    setTempLoading(true)
    try {
      const filterParams = buildFilterParams(tempFilters, newSort)
      const baseParams = `page=${tempPage}&per_page=${perPage}`
      const url = filterParams ? `/jobs/manage.json?${baseParams}&${filterParams}` : `/jobs/manage.json?${baseParams}`
      const data = await fetchManageJson(url)
      setFetchError(null)
      setState(prev => ({
        ...prev,
        temp_jobs: data.temp_jobs.map(job => ({
          ...job,
          temp_date: job.temp_date === null ? job.published_at : job.temp_date,
          practice: job.practice || { id: job.practice_id },
          job_type: job.job_type
        })),
        tempPagination: data.temp_pagination || prev.tempPagination
      }))
    } catch (error) {
      console.error('Error fetching sorted temp jobs:', error)
      setFetchError(error instanceof Error ? error.message : 'Could not load your jobs')
    } finally {
      setTempLoading(false)
    }
  }, [perPage, tempPage, tempFilters])

  // Disabled initial fetch - data is already loaded from server
  // React.useEffect(() => {
  //   fetchJobs()
  // }, [])

  React.useEffect(() => {
    const intervalId = setInterval(fetchJobs, 30000)
    return () => clearInterval(intervalId)
  }, [fetchJobs])

  return (
    <div className="management-workspace management-workspace--jobs">
      {fetchError && (
        <div
          role="alert"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '1rem',
            flexWrap: 'wrap',
            background: '#fdf2f2',
            border: '1px solid #f5c6cb',
            borderLeft: '4px solid #dc3545',
            borderRadius: '0.5rem',
            padding: '0.75rem 1rem',
            marginBottom: '1rem',
            color: '#58151c',
            fontSize: '0.9rem'
          }}
        >
          <span>
            <i className="fa fa-exclamation-triangle me-2" style={{ color: '#dc3545' }}></i>
            We could not refresh your jobs ({fetchError}). Your list may be out of date.
          </span>
          <button
            type="button"
            onClick={() => { setFetchError(null); fetchJobs() }}
            className="btn btn-sm btn-outline-danger"
            style={{ whiteSpace: 'nowrap' }}
          >
            <i className="fa fa-refresh me-1"></i>
            Retry
          </button>
        </div>
      )}
      <header className="management-workspace__header">
        <div>
          <div className="management-workspace__eyebrow">Hiring workspace</div>
          <h1 className="management-workspace__title">Manage jobs</h1>
          <p className="management-workspace__subtitle">
            Publish roles, track applicant activity, and keep hiring work organized across every location.
          </p>
        </div>
        <div className="management-workspace__actions">
          {jobs_remaining && (
            <span className="management-workspace__capacity">
              <i className="fa fa-layer-group" aria-hidden="true"></i>
              {jobs_remaining.remaining} of {jobs_remaining.total} job slots available
            </span>
          )}
          <a href="/applicants" className="management-workspace__action">
            <i className="fa fa-users" aria-hidden="true"></i>
            View applicants
          </a>
          <a href="/jobs/new" className="management-workspace__action management-workspace__action--primary">
            <i className="fa fa-plus" aria-hidden="true"></i>
            Post a job
          </a>
        </div>
      </header>

      <Tabs
        className="management-tabs"
        defaultIndex={index}
        onSelect={(index, lastIndex, event) => onSelect(index)}
      >
        <TabList className="management-tabs__nav" style={{ '--management-tab-count': 2 } as React.CSSProperties}>
          <Tab className="management-tabs__tab">
            <span className="management-tabs__icon"><i className="fa fa-briefcase" aria-hidden="true"></i></span>
            <span className="management-tabs__copy">
              <span className="management-tabs__label">Permanent jobs</span>
              <span className="management-tabs__meta">Full-time, part-time, and long-term roles</span>
            </span>
            <span className="management-tabs__count">{state.permPagination.total_count}</span>
          </Tab>
          <Tab className="management-tabs__tab management-tabs__tab--temp">
            <span className="management-tabs__icon"><i className="fa fa-clock" aria-hidden="true"></i></span>
            <span className="management-tabs__copy">
              <span className="management-tabs__label">Temp jobs</span>
              <span className="management-tabs__meta">Single-day and short-term coverage</span>
            </span>
            <span className="management-tabs__count">{state.tempPagination.total_count}</span>
          </Tab>
        </TabList>
        <div className="management-panel">
          <TabPanel className="management-panel">
            <ManageJobList
              jobs={state.jobs}
              position_types={position_types}
              job_types={job_types}
              practices={practices}
              temp={false}
              paginationTotalRows={state.permPagination.total_count}
              paginationPerPage={state.permPagination.per_page}
              paginationDefaultPage={state.permPagination.current_page}
              onChangePage={handlePermPageChange}
              onChangeRowsPerPage={handlePerPageChange}
              serverFilters={permFilters}
              onServerFiltersChange={handlePermFiltersChange}
              serverSort={permSort}
              onServerSortChange={handlePermSortChange}
              loading={permLoading}
            />
          </TabPanel>
          <TabPanel className="management-panel">
            <ManageJobList
              jobs={state.temp_jobs}
              position_types={position_types}
              job_types={job_types}
              practices={practices}
              temp={true}
              paginationTotalRows={state.tempPagination.total_count}
              paginationPerPage={state.tempPagination.per_page}
              paginationDefaultPage={state.tempPagination.current_page}
              onChangePage={handleTempPageChange}
              onChangeRowsPerPage={handlePerPageChange}
              serverFilters={tempFilters}
              onServerFiltersChange={handleTempFiltersChange}
              serverSort={tempSort}
              onServerSortChange={handleTempSortChange}
              loading={tempLoading}
            />
          </TabPanel>
        </div>
      </Tabs>
    </div>
  )
}
